import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { concatMap, EMPTY, exhaustMap, map, of, OperatorFunction } from 'rxjs';
import { Option } from '@sapphire/result';

import { EffectsBase } from '@ps/core-data-access';
import { AuthSelectorsFacade } from '@ps/auth-data-access';
import { ToastService } from '@ps/core-overlay';
import {
  SaveTemplatePayloadInput,
  TemplateActions,
  TemplateApiActions,
  TemplateService,
} from '@ps/survey-template-data-access';
import {
  SaveTemplateRequest,
  SurveyPricingSnapshot,
  TEMPLATE_MESSAGES,
} from '@ps/survey-utils';

import { SurveyForm } from '../../utils';
import { selectFeasibilityCpi, selectPricing } from '../insights';

import {
  selectSurvey,
  selectSurveyFormState,
  selectSurveyId,
} from './survey-creation-audience.selectors';

/** A template write paired with the survey snapshot it will send. */
interface TemplateSaveContext<TRequest extends SaveTemplateRequest> {
  readonly request: TRequest;
  readonly input: SaveTemplatePayloadInput;
}

/**
 * Saving the survey on screen as a template, and overwriting the one already
 * applied to it.
 *
 * Orchestrated in this lib because the payload is a snapshot of the survey
 * form, whose state this lib owns — `@ps/survey-template-data-access` already
 * depends on it, so the reverse import would be a cycle; the HTTP call and
 * payload mapping stay in that lib. Kept apart from
 * `SurveyCreationTemplateEffects`, which applies templates: a different concern
 * with different collaborators, and splitting them keeps both classes inside
 * the six-field limit.
 */
@Injectable()
export class SurveyCreationTemplateSaveEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _templateService = inject(TemplateService);
  private readonly _authSelectors = inject(AuthSelectorsFacade);
  private readonly _toastService = inject(ToastService);

  public readonly saveTemplate$ = createEffect(() =>
    this._actions$.pipe(
      ofType(TemplateActions.saveTemplate),
      this._withTemplateSnapshot(),
      exhaustMap(({ input }) =>
        this._templateService
          .saveTemplate(input)
          .pipe(
            this._announce(
              TEMPLATE_MESSAGES.SAVED,
              TemplateApiActions.saveTemplateSuccess,
            ),
          ),
      ),
    ),
  );

  /**
   * Overwrites the template already applied to this survey.
   *
   * `isServiceBuyer` comes off the request rather than the snapshot: it is the
   * template's own classification, and re-sending the current session's would
   * reclassify a template a differently-scoped buyer saved.
   */
  public readonly updateTemplate$ = createEffect(() =>
    this._actions$.pipe(
      ofType(TemplateActions.updateTemplate),
      this._withTemplateSnapshot(),
      exhaustMap(({ request, input }) =>
        this._templateService
          .updateTemplate(request.templateId, {
            ...input,
            isServiceBuyer: request.isServiceBuyer,
          })
          .pipe(
            this._announce(
              TEMPLATE_MESSAGES.UPDATED,
              TemplateApiActions.updateTemplateSuccess,
            ),
          ),
      ),
    ),
  );

  /**
   * Turns a completed write into its success action, toasting either outcome.
   *
   * A failure emits nothing: `notify` already surfaces the message, and no
   * failure action exists because nothing reads a save error.
   */
  private _announce<TAction extends Action>(
    message: string,
    success: () => TAction,
  ): OperatorFunction<void, TAction> {
    return (write$) =>
      write$.pipe(
        map(() => {
          this._toastService.success(message, { keepOpen: false });
          return success();
        }),
        this.psCatchError(() => EMPTY, { notify: true }),
      );
  }

  /**
   * Snapshots the live survey form into the template payload input.
   *
   * Drops the write when there is nothing valid to snapshot — no saved survey,
   * or an invalid form — after toasting the form's own error, exactly as
   * `SurveyCreationSaveEffects` does before a survey save. One pipeline rather
   * than a filter paired with throwing unwraps, so the gate and the narrowing
   * cannot drift apart.
   */
  private _withTemplateSnapshot<
    TAction extends { request: SaveTemplateRequest },
  >(): OperatorFunction<TAction, TemplateSaveContext<TAction['request']>> {
    return (trigger$) =>
      trigger$.pipe(
        concatLatestFrom(() => [
          this._store.select(selectSurveyId),
          this._store.select(selectSurveyFormState),
          this._store.select(selectPricing),
          this._store.select(selectFeasibilityCpi),
          this._store.select(selectSurvey),
        ]),
        concatMap(
          ([
            { request },
            surveyId,
            formState,
            pricingState,
            feasibilityCpi,
            survey,
          ]) => {
            const form = SurveyForm.from(formState);
            if (form.showErrors()) {
              Option.from(form.error).inspect((message) =>
                this._toastService.error(message),
              );
            }

            return Option.from(surveyId)
              .filter(() => form.valid && form.isStandardSurvey())
              .andThen((savedSurveyId) =>
                Option.from(form.data).map(
                  ({ metadata, qualifications, quotas }) => ({
                    request,
                    input: {
                      request,
                      surveyId: savedSurveyId,
                      survey: {
                        metadata,
                        userId: this._authSelectors.user().unwrap().id,
                        companyId: this._authSelectors.activeCompanyId(),
                        pricing: {
                          ...pricingState,
                          feasibilityCpi,
                        } satisfies SurveyPricingSnapshot,
                        targeting: { qualifications, quotas },
                      },
                      isFeotEnabled: Option.from(survey).mapOr(
                        false,
                        (loaded) => loaded.isFeotEnabled,
                      ),
                      isServiceBuyer:
                        this._authSelectors.hasImpersonatedBuyer(),
                    } satisfies SaveTemplatePayloadInput,
                  }),
                ),
              )
              .match({
                some: (context) => of(context),
                none: () => EMPTY,
              });
          },
        ),
      );
  }
}
